import { useState, useRef, useEffect } from 'react';
import { Button, Textarea } from '@coordio/ui';
import { Bot, Send as SendIcon } from 'lucide-react';
import { ExecutionTimeline } from './ExecutionTimeline';
import { useOrchestratorStream } from './useOrchestratorStream';
import { sessionService } from '../../services/sessionService';
import { useNotificationStore } from '../../store/notificationStore';

interface OrchestratorChatProps {
  agentId: string;
  agentName: string;
}

export const OrchestratorChat = ({ agentId, agentName }: OrchestratorChatProps) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const { events, isRunning, streamingText, reset } = useOrchestratorStream(sessionId);
  const { showNotification } = useNotificationStore();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events.length, streamingText]);

  const handleSend = async () => {
    if (!input.trim() || sending || isRunning) return;

    try {
      setSending(true);
      let sid = sessionId;

      if (!sid) {
        const session = await sessionService.createSession({
          agentDefinitionId: agentId,
          sessionName: `Orchestrator: ${agentName}`,
        });
        sid = session.id;
        setSessionId(sid);
      }

      reset();
      await sessionService.sendMessage(sid, { content: input });
      setInput('');
    } catch (err: any) {
      showNotification(err.message || 'Failed to send message', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const busy = sending || isRunning;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden" data-testid="orchestrator-chat">
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto bg-[var(--color-bg-surface)]">
        {events.length === 0 && !isRunning && (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <span
              className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-accent-subtle)] text-[var(--color-accent-primary)]"
              aria-hidden
            >
              <Bot size={22} />
            </span>
            <div className="max-w-md space-y-1">
              <p className="text-sm font-medium text-[var(--color-text-primary)]">Ready to orchestrate</p>
              <p className="text-xs leading-relaxed text-[var(--color-text-secondary)]">
                Send a task to <span className="font-medium text-[var(--color-text-primary)]">{agentName}</span>. It will
                delegate work to attached sub-agents via tool calls.
              </p>
            </div>
          </div>
        )}
        <ExecutionTimeline events={events} streamingText={streamingText} isRunning={isRunning} />
      </div>

      <div className="shrink-0 border-t border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-3">
        <div className="flex items-end gap-2 rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-inset)] px-3 py-2 focus-within:border-[var(--color-accent-primary)]">
          <Textarea
            className="min-h-[40px] flex-1 resize-none border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe your task…"
            disabled={busy}
            data-testid="orchestrator-chat-input"
          />
          <Button
            type="button"
            variant="default"
            size="icon"
            onClick={handleSend}
            disabled={!input.trim() || busy}
            className="shrink-0"
            aria-label="Send task"
            data-testid="orchestrator-chat-send"
          >
            <SendIcon size={16} />
          </Button>
        </div>
        <p className="mt-1.5 text-[11px] text-[var(--color-text-secondary)]">
          Enter to send · Shift+Enter for a new line
        </p>
      </div>
    </div>
  );
};
